import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, CircleCheck } from '@saleshandy/icons';
import { Spinner } from 'react-bootstrap';
import classNames from 'classnames';

type LeadFinderVersionDropdownProps = {
  currentVersion: 'v1' | 'v2';
  onVersionChange: (version: 'v1' | 'v2') => void;
  isLoading?: boolean;
};

const versions = [
  { key: 'v2' as const, label: 'New' },
  { key: 'v1' as const, label: 'Old' },
];

const LeadFinderVersionDropdown: React.FC<LeadFinderVersionDropdownProps> = ({
  currentVersion,
  onVersionChange,
  isLoading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentVersionLabel =
    versions.find((v) => v.key === currentVersion)?.label || 'Old';

  // Close dropdown when loading starts
  useEffect(() => {
    if (isLoading) {
      setIsOpen(false);
    }
  }, [isLoading]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleVersionSelect = (version: 'v1' | 'v2') => {
    if (version !== currentVersion && !isLoading) {
      onVersionChange(version);
    }
    setIsOpen(false);
  };

  return (
    <div className="lead-finder-version-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className={classNames('lead-finder-version-dropdown__trigger', {
          'lead-finder-version-dropdown__trigger--open': isOpen,
          'lead-finder-version-dropdown__trigger--loading': isLoading,
        })}
        onClick={() => !isLoading && setIsOpen(!isOpen)}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Spinner
              animation="border"
              size="sm"
              className="lead-finder-version-dropdown__spinner"
            />
            <span className="lead-finder-version-dropdown__label">
              Switching...
            </span>
          </>
        ) : (
          <>
            <span className="lead-finder-version-dropdown__label">
              {currentVersionLabel}
            </span>
            <ChevronDown
              width={16}
              height={16}
              className={classNames('lead-finder-version-dropdown__chevron', {
                'lead-finder-version-dropdown__chevron--rotated': isOpen,
              })}
            />
          </>
        )}
      </button>

      {isOpen && !isLoading && (
        <div className="lead-finder-version-dropdown__menu">
          {versions.map((version) => (
            <button
              key={version.key}
              type="button"
              className={classNames('lead-finder-version-dropdown__item', {
                'lead-finder-version-dropdown__item--selected':
                  version.key === currentVersion,
              })}
              onClick={() => handleVersionSelect(version.key)}
            >
              <span className="lead-finder-version-dropdown__item-label">
                {version.label}
              </span>
              {version.key === currentVersion && (
                <CircleCheck
                  width={16}
                  height={16}
                  className="lead-finder-version-dropdown__item-check"
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeadFinderVersionDropdown;

